import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { playSound } from "../../soundEffects";

/* ---------------- OFFSET HELPER ---------------- */
function getGotiOffset(index, total) {
  if (total === 1) return { dx: 0, dy: 0, scale: 1 };

  const scale = total === 2 ? 0.70 : total === 3 ? 0.62 : 0.56;
  const gap = 14 * scale * 1.2;
  const totalSpan = (total - 1) * gap;
  const offset = index * gap - totalSpan / 2;
  const cos45 = Math.SQRT1_2;

  return { dx: offset * cos45, dy: offset * cos45, scale };
}

/* ---------------- JUMP + SQUASH ---------------- */
function getJumpTransform(from, to, t) {
  const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

  const x = from.x + (to.x - from.x) * ease;
  const y = from.y + (to.y - from.y) * ease;

  const lift = Math.sin(Math.PI * t) * 28;

  let scaleX = 1;
  let scaleY = 1;

  if (t > 0.85) {
    const squash = (t - 0.85) / 0.15;
    scaleX = 1 + squash * 0.25;
    scaleY = 1 - squash * 0.25;
  }

  return { x, y: y - lift, scaleX, scaleY };
}

/* ---------------- EAT HINT ARROW ---------------- */
function EatHintArrow({ fromCoord, toCoord, color }) {
  const [fx, fy] = fromCoord;
  const [tx, ty] = toCoord;
  const dx = tx - fx;
  const dy = ty - fy;
  const angle = Math.atan2(dy, dx);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const NUM = 3;
  const DUR = 1.0;
  const CS  = 7;
  const SW  = 2.5;

  // Pre-rotate chevron points at origin (from), pure translate does the travel
  const rp  = (px, py) => [fx + px * cos - py * sin, fy + px * sin + py * cos];
  const [ax, ay] = rp(-CS, -CS * 0.6);
  const [bx, by] = rp(0,   0);
  const [ex, ey] = rp(-CS,  CS * 0.6);
  const pts = `${ax},${ay} ${bx},${by} ${ex},${ey}`;

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* dashed guide line */}
      <line
        x1={fx} y1={fy} x2={tx} y2={ty}
        stroke={color} strokeWidth="1.2"
        strokeDasharray="5 4" opacity="0.3"
      />
      {/* flowing chevrons: all start at fromCoord, staggered in time */}
      {Array.from({ length: NUM }, (_, i) => (
        <polyline
          key={i}
          points={pts}
          fill="none"
          stroke={color}
          strokeWidth={SW}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0}
        >
          <animateTransform
            attributeName="transform"
            type="translate"
            from="0 0"
            to={`${dx} ${dy}`}
            dur={`${DUR}s`}
            begin={`${-(i / NUM) * DUR}s`}
            repeatCount="indefinite"
            calcMode="linear"
          />
          <animate
            attributeName="opacity"
            values="0;0;1;1;0;0"
            keyTimes="0;0.05;0.18;0.82;0.95;1"
            dur={`${DUR}s`}
            begin={`${-(i / NUM) * DUR}s`}
            repeatCount="indefinite"
          />
        </polyline>
      ))}
    </g>
  );
}

function parsePoints(points) {
  return points.split(" ").map((point) => {
    const [x, y] = point.split(",").map(Number);
    return { x, y };
  });
}

function pointInQuad([p1, p2, p3, p4], u, v) {
  return {
    x:
      (1 - u) * (1 - v) * p1.x +
      (1 - u) * v * p2.x +
      u * v * p3.x +
      u * (1 - v) * p4.x,
    y:
      (1 - u) * (1 - v) * p1.y +
      (1 - u) * v * p2.y +
      u * v * p3.y +
      u * (1 - v) * p4.y,
  };
}


/* ---------------- COMPONENT ---------------- */
function LudoMarkerGoti({
  winBoxCordLine,
  gotis,
  markers,
  homeMarkers,
  circleCoordinates,
  moveRequest,
  onMoveComplete,
  numberWiseColor,
  currentPlayer,
  localPlayerId,
  handleAnimation,
  PLAYERS,
  movableGotis,
  eatHints,
  captureRequest,
  onCaptureComplete,
  finishRequest,
  onFinishComplete,
  lastStandGotiId,
  teamLastStandGotiId,
  safeCellEffect = null,
  rankings = [],
}) {
  const [activeGotiId, setActiveGotiId] = useState(null);
  const [stepsLeft, setStepsLeft] = useState(0);
  const [animatedBox, setAnimatedBox] = useState(null);
  const [victoryDanceId, setVictoryDanceId] = useState(null);

  // ✅ Stable refs to avoid animation restarts
  const activeGotiRef = useRef(null);
  const activeShadowRef = useRef(null);
  const rafRef = useRef(null);
  const movePathRef = useRef([]);
  const startPositionRef = useRef(null);
  const stepsLeftRef = useRef(0);
  const onMoveCompleteRef = useRef(onMoveComplete);
  const openedFromHomeRef = useRef(false);

  const captureOverlayRef = useRef(null);
  const captureGotiRef = useRef(null);
  const captureEmojiRef = useRef(null);
  const captureRafRef = useRef(null);
  const onCaptureCompleteRef = useRef(onCaptureComplete);
  const attackerGotiIdRef = useRef(null);
  const victoryTimerRef = useRef(null);

  const finishOverlayRef = useRef(null);
  const finishGotiRef = useRef(null);
  const finishRafRef = useRef(null);
  const onFinishCompleteRef = useRef(onFinishComplete);

  useEffect(() => {
    onMoveCompleteRef.current = onMoveComplete;
  }, [onMoveComplete]);

  useEffect(() => {
    onCaptureCompleteRef.current = onCaptureComplete;
  }, [onCaptureComplete]);

  useEffect(() => {
    onFinishCompleteRef.current = onFinishComplete;
  }, [onFinishComplete]);

  /* ---------- VICTORY DANCE — fires when capture animation completes ---------- */
  useEffect(() => {
    if (captureRequest) {
      attackerGotiIdRef.current = captureRequest.attackerGotiId ?? null;
    } else if (attackerGotiIdRef.current) {
      const attackerId = attackerGotiIdRef.current;
      attackerGotiIdRef.current = null;
      clearTimeout(victoryTimerRef.current);
      setVictoryDanceId(attackerId);
      victoryTimerRef.current = setTimeout(() => setVictoryDanceId(null), 1400);
    }
  }, [captureRequest]);

  /* ---------- CLEAR ACTIVE GOTI WHEN MOVE REQUEST IS SETTLED ---------- */
  useEffect(() => {
    if (moveRequest) return;
    setActiveGotiId(null);
    setAnimatedBox(null);
  }, [moveRequest]);

  /* ---------- INIT MOVE ---------- */
  useEffect(() => {
    if (!moveRequest) return;

    const g = gotis.find((g) => g.id === moveRequest.gotiId);
    if (!g) return;

    const player = PLAYERS.find((p) => p.id === g.playerId);

    // ✅ Set refs first so animation loop reads correct values immediately
    movePathRef.current = moveRequest.path || [];
    startPositionRef.current = g.position;
    stepsLeftRef.current = moveRequest.steps;
    openedFromHomeRef.current = Boolean(moveRequest.openedFromHome);

    setActiveGotiId(moveRequest.gotiId);
    setStepsLeft(moveRequest.steps);

    if (g.position === -1) {
      setAnimatedBox(player.start - 1);
    } else {
      setAnimatedBox(g.position);
    }
  }, [moveRequest, gotis, PLAYERS]);

  /* ---------- STEP BY STEP MOVE ---------- */
  useEffect(() => {
    if (!activeGotiId || stepsLeft <= 0 || animatedBox === null) return;

    const fromBox = animatedBox;
    const nextBox = movePathRef.current[0];

    // Guard: no next step to go to
    if (nextBox === undefined && nextBox !== 0) return;

    if (!markers[fromBox] || !markers[nextBox]) {
      // Safety: markers missing, finish animation immediately
      movePathRef.current = [];
      stepsLeftRef.current = 0;
      activeGotiRef.current?.removeAttribute("transform");
      if (activeShadowRef.current) {
        activeShadowRef.current.style.opacity = 0;
      }
      setAnimatedBox(nextBox);
      setStepsLeft(0);
      onMoveCompleteRef.current(activeGotiId, nextBox);
      setActiveGotiId(null);
      return;
    }

    const from = {
      x: markers[fromBox].headCircle[0],
      y: markers[fromBox].headCircle[1],
    };

    const to = {
      x: markers[nextBox].headCircle[0],
      y: markers[nextBox].headCircle[1],
    };
    const baseBox =
      startPositionRef.current === -1 ? fromBox : startPositionRef.current;
    const baseMarker = markers[baseBox];
    const activeNode = activeGotiRef.current;
    const shadowNode = activeShadowRef.current;

    if (!baseMarker || !activeNode) return;

    const base = {
      x: baseMarker.headCircle[0],
      y: baseMarker.headCircle[1],
    };

    if (!openedFromHomeRef.current) {
      playSound("step");
    }

    let start = null;
    const duration = 260;

    function frame(time) {
      if (!start) start = time;
      const t = Math.min((time - start) / duration, 1);
      const jump = getJumpTransform(from, to, t);

      activeNode.setAttribute(
        "transform",
        `
          translate(${jump.x - base.x}, ${jump.y - base.y})
          translate(${base.x}, ${base.y})
          scale(${jump.scaleX}, ${jump.scaleY})
          translate(${-base.x}, ${-base.y})
        `,
      );

      if (shadowNode) {
        shadowNode.style.opacity = t < 1 ? 1 : 0;
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        if (shadowNode) {
          shadowNode.style.opacity = 0;
        }

        // ✅ Update refs immediately (no re-render delay)
        movePathRef.current = movePathRef.current.slice(1);
        const remaining = stepsLeftRef.current - 1;
        const shouldClearHomeTransform = startPositionRef.current === -1;
        stepsLeftRef.current = remaining;
        setStepsLeft(remaining);
        setAnimatedBox(nextBox);

        if (remaining === 0) {
          onMoveCompleteRef.current(activeGotiId, nextBox);
          movePathRef.current = [];
          // Do NOT clear activeGotiId here — wait until moveRequest is nulled
          // by applyGameUpdate so the goti stays at its final position if the
          // game update is deferred (eat / finish animation pending).
        }

        if (shouldClearHomeTransform) {
          requestAnimationFrame(() => {
            activeGotiRef.current?.removeAttribute("transform");
          });
        }
      }
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (shadowNode) {
        shadowNode.style.opacity = 0;
      }
    };

    // ✅ Stable deps only — movePath/onMoveComplete/gotis removed to prevent animation restarts
  }, [activeGotiId, stepsLeft, animatedBox, markers]);

  /* ---------- CAPTURE ANIMATION INFO ---------- */
  const captureInfo = useMemo(() => {
    if (!captureRequest) return null;
    const { gotiId, fromCell } = captureRequest;
    const goti = gotis.find((g) => g.id === gotiId);
    if (!goti || !markers[fromCell]) return null;
    const [fromX, fromY] = markers[fromCell].headCircle;
    const slotIndex = Number(String(gotiId).split("-")[1]);
    const homeSlot = circleCoordinates[goti.playerId]?.[slotIndex];
    if (!homeSlot) return null;
    return {
      gotiId,
      playerId: goti.playerId,
      fromX,
      fromY,
      toX: homeSlot[0] - 40,
      toY: homeSlot[1],
    };
  }, [captureRequest, gotis, markers, circleCoordinates]);

  const captureInfoRef = useRef(captureInfo);
  captureInfoRef.current = captureInfo;

  /* ---------- CAPTURE ANIMATION LOOP ---------- */
  useLayoutEffect(() => {
    if (!captureInfo) return;
    const outerNode = captureOverlayRef.current;
    const gotiNode = captureGotiRef.current;
    if (!outerNode || !gotiNode) return;

    cancelAnimationFrame(captureRafRef.current);

    const { fromX, fromY, toX, toY } = captureInfo;

    // Phase durations (ms)
    const IMPACT = 450;   // burst + flash
    const RECOIL = 180;   // snap back, start spinning
    const FLY    = 750;   // arc flight home with rotation
    const APPEAR = 350;   // materialize + bounce at home
    const TOTAL  = IMPACT + RECOIL + FLY + APPEAR;

    const particles = Array.from(outerNode.querySelectorAll(".cp"));
    const rings     = Array.from(outerNode.querySelectorAll(".cr"));
    let startTime = null;
    let homeSoundPlayed = false;

    function frame(time) {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const t = Math.min(elapsed / TOTAL, 1);

      if (elapsed < IMPACT) {
        // ---- IMPACT: scale up like a shockwave, red-orange flash ----
        const p = elapsed / IMPACT;
        const impactScale = 1 + Math.sin(p * Math.PI) * 1.8;
        const brightness  = 1 + (1 - p) * 2.5;
        const hue         = Math.round((1 - p) * -60); // toward red

        outerNode.style.transform = `translate(${fromX}px, ${fromY}px)`;
        outerNode.style.opacity   = "1";
        gotiNode.style.transform  = `scale(${impactScale})`;
        gotiNode.style.filter     = `hue-rotate(${hue}deg) brightness(${brightness}) saturate(3)`;

        // Crying emoji floats upward
        const emojiNode = captureEmojiRef.current;
        if (emojiNode) {
          const emojiY = -20 - p * 35;
          const emojiOpacity = p < 0.25 ? p / 0.25 : p < 0.65 ? 1 : 1 - (p - 0.65) / 0.35;
          emojiNode.setAttribute("y", emojiY);
          emojiNode.style.opacity = Math.max(0, emojiOpacity);
        }

        // Burst particles radiate outward then fade
        particles.forEach((el, i) => {
          const angle  = (i / particles.length) * Math.PI * 2;
          const dist   = p * 75;
          const pscale = Math.max(0, 1 - p * 0.8);
          const pop    = Math.max(0, 1 - p * 1.3);
          el.setAttribute("cx", Math.cos(angle) * dist);
          el.setAttribute("cy", Math.sin(angle) * dist);
          el.setAttribute("r", 7 * pscale);
          el.style.opacity = pop;
        });

        // Shockwave rings: each has a stagger delay (0ms, 120ms)
        rings.forEach((el, i) => {
          const delay = i * 120;
          const rp = Math.max(0, (elapsed - delay) / (IMPACT * 0.8));
          if (rp <= 0) {
            el.style.opacity = "0";
            return;
          }
          const easeRp = 1 - Math.pow(1 - Math.min(rp, 1), 2);
          el.setAttribute("r", 14 + easeRp * 85);
          el.style.opacity = String(Math.max(0, 1 - rp * 1.1));
        });

      } else if (elapsed < IMPACT + RECOIL) {
        rings.forEach((el) => { el.style.opacity = "0"; });
        if (captureEmojiRef.current) captureEmojiRef.current.style.opacity = "0";
        // ---- RECOIL: snaps back, begins spinning ----
        const p   = (elapsed - IMPACT) / RECOIL;
        const sc  = 1 - p * 0.35;
        const rot = p * 200;

        outerNode.style.transform = `translate(${fromX}px, ${fromY}px)`;
        gotiNode.style.transform  = `scale(${sc}) rotate(${rot}deg)`;
        gotiNode.style.filter     = "";
        particles.forEach((el) => { el.style.opacity = "0"; });
        rings.forEach((el) => { el.style.opacity = "0"; });

      } else if (elapsed < IMPACT + RECOIL + FLY) {
        // ---- FLY: arc path home, full spinning ----
        const p    = (elapsed - IMPACT - RECOIL) / FLY;
        const ease = 1 - Math.pow(1 - p, 2);

        const dx     = toX - fromX;
        const dy     = toY - fromY;
        const len    = Math.sqrt(dx * dx + dy * dy) || 1;
        const perpX  = -dy / len;
        const perpY  =  dx / len;
        const bulge  = Math.sin(p * Math.PI) * 90; // arc height

        const cx  = fromX + dx * ease + perpX * bulge;
        const cy  = fromY + dy * ease + perpY * bulge;
        const sc  = Math.max(0.1, 0.65 - ease * 0.45);
        const rot = 200 + ease * 580; // continues from recoil, ~1.5 spins

        outerNode.style.transform = `translate(${cx}px, ${cy}px)`;
        outerNode.style.opacity   = "1";
        gotiNode.style.transform  = `scale(${sc}) rotate(${rot}deg)`;
        gotiNode.style.filter     = "";

      } else {
        // ---- APPEAR: materialise + bounce at home slot ----
        const p = (elapsed - IMPACT - RECOIL - FLY) / APPEAR;

        if (!homeSoundPlayed) {
          homeSoundPlayed = true;
          playSound("land");
        }

        const ease = 1 - Math.pow(1 - p, 2);
        // overshoot bounce: 0 → 1.3 → 1.0
        const sc   = p < 0.65
          ? (ease / (1 - Math.pow(0.35, 2))) * 1.3
          : 1.3 - ((p - 0.65) / 0.35) * 0.3;

        outerNode.style.transform = `translate(${toX}px, ${toY}px)`;
        outerNode.style.opacity   = String(Math.min(1, ease * 2));
        gotiNode.style.transform  = `scale(${Math.max(0.05, sc)})`;
        gotiNode.style.filter     = "";
      }

      if (t < 1) {
        captureRafRef.current = requestAnimationFrame(frame);
      } else {
        outerNode.style.opacity = "0";
        onCaptureCompleteRef.current?.();
      }
    }

    captureRafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(captureRafRef.current);
  }, [captureRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- FINISH ANIMATION INFO ---------- */
  const finishInfo = useMemo(() => {
    if (!finishRequest) return null;
    const { gotiId, fromCell } = finishRequest;
    const goti = gotis.find((g) => g.id === gotiId);
    if (!goti || !markers[fromCell]) return null;

    const [fromX, fromY] = markers[fromCell].headCircle;

    const numericPlayerId = Number(goti.playerId);
    const playerCount = numberWiseColor.length;
    const winBoxIndex = (numericPlayerId - 1 + playerCount) % playerCount;
    const winBox = winBoxCordLine?.[winBoxIndex];
    if (!winBox) return null;

    const quad = parsePoints(winBox);
    const slots = [[0.34, 0.36], [0.66, 0.36], [0.34, 0.68], [0.66, 0.68]];
    const alreadyFinished = gotis.filter(
      (g) => g.finished && g.playerId === goti.playerId,
    ).length;
    const [u, v] = slots[alreadyFinished % slots.length];
    const { x: toX, y: toY } = pointInQuad(quad, u, v);

    const label = `${alreadyFinished + 1}`;

    return { gotiId, playerId: goti.playerId, fromX, fromY, toX, toY, label };
  }, [finishRequest, gotis, markers, winBoxCordLine, numberWiseColor]);

  /* ---------- FINISH ANIMATION LOOP ---------- */
  useLayoutEffect(() => {
    if (!finishInfo) return;
    const outerNode = finishOverlayRef.current;
    const gotiNode = finishGotiRef.current;
    if (!outerNode || !gotiNode) return;

    cancelAnimationFrame(finishRafRef.current);

    const { fromX, fromY, toX, toY } = finishInfo;

    const FLY   = 850;  // spin + arc flight to win box
    const BURST = 500;  // star shockwave + goti bounce settle
    const TOTAL = FLY + BURST;

    const rays  = Array.from(outerNode.querySelectorAll(".fr"));
    const rings = Array.from(outerNode.querySelectorAll(".fring"));
    let startTime = null;

    function frame(time) {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const t = Math.min(elapsed / TOTAL, 1);

      if (elapsed < FLY) {
        // ---- FLY: arc + spin toward win box ----
        const p    = elapsed / FLY;
        const ease = 1 - Math.pow(1 - p, 2);

        const dx    = toX - fromX;
        const dy    = toY - fromY;
        const len   = Math.sqrt(dx * dx + dy * dy) || 1;
        const perpX = -dy / len;
        const perpY =  dx / len;
        const bulge = Math.sin(p * Math.PI) * 80;

        const cx  = fromX + dx * ease + perpX * bulge;
        const cy  = fromY + dy * ease + perpY * bulge;
        const sc  = 0.9 - ease * 0.5;
        const rot = ease * 720;

        outerNode.style.transform = `translate(${cx}px, ${cy}px)`;
        outerNode.style.opacity   = "1";
        gotiNode.style.transform  = `scale(${Math.max(0.1, sc)}) rotate(${rot}deg)`;
        gotiNode.style.filter     = "";
        rays.forEach((el)  => { el.style.opacity = "0"; });
        rings.forEach((el) => { el.style.opacity = "0"; });

      } else {
        // ---- BURST: star rays + rings expand, goti bounces in ----
        const p    = (elapsed - FLY) / BURST;
        const ease = 1 - Math.pow(1 - p, 3);

        // Goti bounce: 0 → 1.35 → 1.0
        const sc = p < 0.5
          ? (p / 0.5) * 1.35
          : 1.35 - ((p - 0.5) / 0.5) * 0.35;

        outerNode.style.transform = `translate(${toX}px, ${toY}px)`;
        outerNode.style.opacity   = "1";
        gotiNode.style.transform  = `scale(${Math.max(0.05, sc)})`;
        gotiNode.style.filter     = "";

        // Star rays extend outward from circle edge
        rays.forEach((el, i) => {
          const angle = (i / rays.length) * Math.PI * 2;
          const inner = 15;
          const outer = inner + ease * 60;
          el.setAttribute("x1", Math.cos(angle) * inner);
          el.setAttribute("y1", Math.sin(angle) * inner);
          el.setAttribute("x2", Math.cos(angle) * outer);
          el.setAttribute("y2", Math.sin(angle) * outer);
          el.style.opacity = String(Math.max(0, 1 - p * 1.3));
        });

        // Shockwave rings with stagger
        rings.forEach((el, i) => {
          const delay = i * 80;
          const rp = Math.max(0, (elapsed - FLY - delay) / (BURST * 0.85));
          if (rp <= 0) { el.style.opacity = "0"; return; }
          const easeRp = Math.min(1, 1 - Math.pow(1 - rp, 2));
          el.setAttribute("r", 14 + easeRp * 70);
          el.style.opacity = String(Math.max(0, 1 - rp * 1.1));
        });
      }

      if (t < 1) {
        finishRafRef.current = requestAnimationFrame(frame);
      } else {
        outerNode.style.opacity = "0";
        onFinishCompleteRef.current?.();
      }
    }

    finishRafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(finishRafRef.current);
  }, [finishRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  function isSelectable(goti) {
    return (
      goti.playerId === currentPlayer &&
      goti.playerId === localPlayerId &&
      !goti.finished
    );
  }

  function isActiveTurn(goti) {
    return goti.playerId === currentPlayer && !goti.finished;
  }

  /* ---------- GROUP GOTIS ---------- */
  const { boardGotisByCell, homeGotisByPlayer, winGotisByPlayer } = useMemo(() => {
    const board = {};
    const home = {};
    const win = {};
    gotis.forEach((g) => {
      if (g.id === captureRequest?.gotiId) return;
      if (g.id === finishRequest?.gotiId) return;
      if (g.id === activeGotiId && g.position === -1 && animatedBox !== null) {
        if (!board[animatedBox]) board[animatedBox] = [];
        board[animatedBox].push(g);
      } else if (g.finished) {
        if (!win[g.playerId]) win[g.playerId] = [];
        win[g.playerId].push(g);
      } else if (g.position >= 0) {
        if (!board[g.position]) board[g.position] = [];
        board[g.position].push(g);
      } else {
        if (!home[g.playerId]) home[g.playerId] = [];
        home[g.playerId].push(g);
      }
    });
    return { boardGotisByCell: board, homeGotisByPlayer: home, winGotisByPlayer: win };
  }, [gotis, captureRequest, finishRequest, activeGotiId, animatedBox]);

  /* ---------- RENDER ---------- */
  return (
    <>
      <defs />

      {/* -------- HOME SLOT RINGS (always rendered, independent of goti position) -------- */}
      {Object.entries(circleCoordinates).map(([playerId, circles]) => {
        const color = numberWiseColor[Number(playerId)];
        // Only spin rings for the active player — 32 → ~8 live animations
        const isActive = Number(playerId) === currentPlayer;
        return circles.map((coord, slotIndex) => {
          const rcx = coord[0] - 4;
          const rcy = coord[1];
          return (
            <g key={`ring-${playerId}-${slotIndex}`} style={{ pointerEvents: "none" }}>
              <circle cx={rcx} cy={rcy} r={17} fill="none"
                stroke={color} strokeWidth="3" strokeDasharray="5 9" opacity={isActive ? 0.7 : 0.35}>
                {isActive && (
                  <animateTransform attributeName="transform" type="rotate"
                    from={`0 ${rcx} ${rcy}`} to={`360 ${rcx} ${rcy}`}
                    dur="3.5s" repeatCount="indefinite" />
                )}
              </circle>
              <circle cx={rcx} cy={rcy} r={12} fill="none"
                stroke={color} strokeWidth="2" strokeDasharray="3 9" opacity={isActive ? 0.5 : 0.2}>
                {isActive && (
                  <animateTransform attributeName="transform" type="rotate"
                    from={`0 ${rcx} ${rcy}`} to={`-360 ${rcx} ${rcy}`}
                    dur="2.2s" repeatCount="indefinite" />
                )}
              </circle>
            </g>
          );
        });
      })}

      {/* -------- HOME RANK BADGES (shown when player completes all gotis) -------- */}
      {rankings.length > 0 && Object.entries(circleCoordinates).map(([playerId, circles]) => {
        const pId = Number(playerId);
        const rank = rankings.indexOf(pId) + 1;
        if (rank === 0) return null;

        const cx = circles.reduce((s, [x]) => s + (x - 40), 0) / circles.length;
        const cy = circles.reduce((s, [, y]) => s + y, 0) / circles.length;
        const isFirst = rank === 1;
        const label = isFirst ? "1st" : rank === 2 ? "2nd" : rank === 3 ? "3rd" : `${rank}th`;

        return (
          <g key={`rank-badge-${playerId}`} style={{ pointerEvents: "none" }}>
            <rect
              x={cx - 20} y={cy - (isFirst ? 18 : 11)}
              width={40} height={isFirst ? 36 : 22}
              rx={6}
              fill="rgba(0,0,0,0.72)"
              stroke={isFirst ? "#facc15" : "rgba(255,255,255,0.35)"}
              strokeWidth={isFirst ? 1.5 : 1}
            />
            {isFirst && (
              <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="central" fontSize="13" style={{ userSelect: "none" }}>
                👑
              </text>
            )}
            <text
              x={cx} y={isFirst ? cy + 10 : cy + 1}
              textAnchor="middle" dominantBaseline="central"
              fontSize="10" fontWeight="bold"
              fill={isFirst ? "#facc15" : "white"}
              style={{ userSelect: "none" }}
            >
              {label}
            </text>
          </g>
        );
      })}

      {/* -------- HOME GOTIS (TRIANGLE) -------- */}
      {Object.entries(homeGotisByPlayer).map(([playerId, homeGotis]) => {
        const circles = circleCoordinates[playerId];
        if (!circles) return null;

        return homeGotis.map((goti) => {
          const slotIndex = Number(String(goti.id).split("-")[1]);
          const [x, y] = circles[slotIndex];
          const homeMarker = homeMarkers[playerId][slotIndex];

          const gx = x - 40;
          const gy = y;
          const color = numberWiseColor[goti.playerId];

          return (
            <g key={goti.id}>
              <polygon
                points={homeMarker.headCoordinates}
                fill="grey"
                stroke="black"
              />

              <circle
                className={
                  movableGotis?.includes(goti.id) && !moveRequest
                    ? "movableGotiPulse"
                    : ""
                }
                cx={gx}
                cy={gy}
                r={12}
                fill={color}
                stroke="black"
                style={{
                  cursor: isSelectable(goti) ? "pointer" : "default",
                  opacity: isSelectable(goti) ? 1 : 0.4,
                }}
                onClick={() => {
                  if (!isSelectable(goti) || moveRequest) return;
                  handleAnimation(goti.id);
                }}
              />
            </g>
          );
        });
      })}

      {/* -------- FINISHED GOTIS (CENTER WIN BOX) -------- */}
      {Object.entries(winGotisByPlayer).map(([playerId, winGotis]) => {
        const numericPlayerId = Number(playerId);
        const playerCount = numberWiseColor.length;
        const winBoxIndex = (numericPlayerId - 1 + playerCount) % playerCount;
        const winBox = winBoxCordLine?.[winBoxIndex];
        if (!winBox) return null;

        const quad = parsePoints(winBox);

        const sorted = [...winGotis].sort(
          (a, b) => (a.finishOrder ?? 99) - (b.finishOrder ?? 99),
        );

        const n = sorted.length;
        // Scale radius down so more gotis fit, allow slight overlap
        const r = n <= 1 ? 13 : n === 2 ? 12 : n === 3 ? 10 : 9;
        // Horizontal positions spread across the box center row
        const uPositions = n === 1
          ? [0.5]
          : Array.from({ length: n }, (_, i) => 0.15 + (i / (n - 1)) * 0.7);

        return sorted.map((goti, index) => {
          const { x, y } = pointInQuad(quad, uPositions[index], 0.5);
          const label = goti.finishOrder ?? index + 1;

          return (
            <g key={goti.id}>
              <circle
                cx={x}
                cy={y}
                r={r}
                fill={numberWiseColor[goti.playerId]}
                stroke="white"
                strokeWidth="2"
              />
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={r}
                fontWeight="900"
                fill="white"
                stroke="rgba(0,0,0,0.5)"
                strokeWidth="0.6"
                paintOrder="stroke"
              >
                {label}
              </text>
            </g>
          );
        });
      })}

      {/* -------- BOARD GOTIS -------- */}
      {Object.entries(boardGotisByCell).map(([boxIndex, cellGotis]) => {
        const marker = markers[boxIndex];
        if (!marker) return null;

        const baseX = marker.headCircle[0];
        const baseY = marker.headCircle[1];

        // Render highlighted (movable) goti last so it paints on top
        const orderedGotis = movableGotis && !moveRequest
          ? [...cellGotis].sort((a, b) => {
              const aH = movableGotis.includes(a.id);
              const bH = movableGotis.includes(b.id);
              return aH === bH ? 0 : aH ? 1 : -1;
            })
          : cellGotis;

        return orderedGotis.map((goti) => {
          const index       = cellGotis.indexOf(goti); // stable index for offset calc
          const { dx, dy, scale } = getGotiOffset(index, cellGotis.length);
          const isStacked   = cellGotis.length > 1;
          const isActive    = goti.id === activeGotiId;
          const isMovable   = movableGotis?.includes(goti.id) && !moveRequest;
          const isHighlighted = isStacked && isMovable;

          // Highlighted goti expands to full size at cell center
          const effectiveDx     = isHighlighted ? 0 : dx;
          const effectiveDy     = isHighlighted ? 0 : dy;
          const effectiveScale  = isHighlighted ? 1 : scale;
          const stackShiftX     = isStacked && !isHighlighted ? 20 : 0;
          const cx = baseX + effectiveDx + stackShiftX;
          const cy = baseY + effectiveDy;
          let outerTransform = undefined;

          if (
            isActive &&
            animatedBox !== null &&
            animatedBox !== boxIndex &&
            markers[animatedBox]
          ) {
            const dest = markers[animatedBox].headCircle;
            outerTransform = `translate(${dest[0] - baseX}, ${dest[1] - baseY})`;
          }

          const scaleTransform = isStacked && !isHighlighted
            ? `translate(${cx}, ${cy}) scale(${effectiveScale}) translate(${-cx}, ${-cy})`
            : undefined;

          return (
            <g
              key={goti.id}
              ref={isActive ? activeGotiRef : null}
              transform={outerTransform}
              style={{ cursor: isSelectable(goti) ? "pointer" : "default" }}
              onClick={() => {
                if (!isSelectable(goti) || moveRequest) return;
                handleAnimation(goti.id);
              }}
            >
              {isActive && (
                <ellipse
                  ref={activeShadowRef}
                  cx={cx}
                  cy={cy + 6}
                  rx={12 * effectiveScale}
                  ry={4}
                  fill="rgba(0,0,0,0.25)"
                  style={{ opacity: 0, pointerEvents: "none" }}
                />
              )}

              <g transform={scaleTransform}>
                <polygon
                  points={marker.headCoordinates}
                  fill="white"
                  stroke="black"
                  transform={isStacked && !isHighlighted
                    ? `translate(${effectiveDx + stackShiftX}, ${effectiveDy})`
                    : undefined}
                />
                {/* Last-stand danger ring */}
                {lastStandGotiId === goti.id && (
                  <circle cx={cx} cy={cy} r={20} fill="none" stroke="#ef4444" strokeWidth="2.5" style={{ pointerEvents: "none" }} opacity="0.9">
                    <animate attributeName="r" values="18;26;18" dur="0.85s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.9;0.15;0.9" dur="0.85s" repeatCount="indefinite" />
                    <animate attributeName="stroke-width" values="2.5;1;2.5" dur="0.85s" repeatCount="indefinite" />
                  </circle>
                )}
                {/* Teammate shield badge */}
                {teamLastStandGotiId === goti.id && (
                  <>
                    <circle cx={cx} cy={cy} r={22} fill="none" stroke="#38bdf8" strokeWidth="2" style={{ pointerEvents: "none" }} opacity="0.85">
                      <animate attributeName="r" values="20;28;20" dur="1s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.85;0.2;0.85" dur="1s" repeatCount="indefinite" />
                    </circle>
                    <text x={cx + 13} y={cy - 11} fontSize="13" style={{ pointerEvents: "none", userSelect: "none" }}>🛡️</text>
                  </>
                )}
                <circle
                  className={[
                    isMovable ? "movableGotiPulse" : "",
                    victoryDanceId === goti.id ? "gotiVictoryDance" : "",
                  ].filter(Boolean).join(" ")}
                  cx={cx}
                  cy={cy}
                  r={14}
                  fill={numberWiseColor[goti.playerId]}
                  stroke={isStacked && !isHighlighted ? "white" : "black"}
                  strokeWidth={isStacked && !isHighlighted ? 1.5 : 1}
                  style={{
                    cursor: isSelectable(goti) ? "pointer" : "default",
                    opacity: isActiveTurn(goti) ? 1 : 0.4,
                    transformBox: "fill-box",
                    transformOrigin: "center",
                  }}
                />
              </g>

              {/* Laughing emoji floats up after attacker eats someone */}
              {victoryDanceId === goti.id && (
                <text
                  x={cx}
                  y={cy - 16}
                  textAnchor="middle"
                  fontSize="28"
                  style={{ pointerEvents: "none", userSelect: "none" }}
                >
                  😂
                  <animate
                    attributeName="y"
                    values={`${cy - 16};${cy - 58}`}
                    dur="1.3s"
                    fill="freeze"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;0.08;0.68;1"
                    dur="1.3s"
                    fill="freeze"
                  />
                </text>
              )}
            </g>
          );
        });
      })}

      {/* -------- SAFE CELL LIGHTNING EFFECT -------- */}
      {safeCellEffect && markers[safeCellEffect.position] && (() => {
        const [lx, ly] = markers[safeCellEffect.position].headCircle;
        const bolts = [0, 45, 90, 135, 180, 225, 270, 315];
        return (
          <g key={safeCellEffect.key} transform={`translate(${lx}, ${ly})`} style={{ pointerEvents: "none" }}>
            <circle r="28" fill="none" stroke="#ffe033" strokeWidth="2" className="scGlow" />
            <circle r="20" fill="rgba(255,235,50,0.35)" className="scGlow" />
            <circle r="12" fill="rgba(255,248,100,0.95)" className="scCore" />
            {bolts.map((deg) => {
              const rad = (deg * Math.PI) / 180;
              const cos = Math.cos(rad);
              const sin = Math.sin(rad);
              const perp = rad + Math.PI / 2;
              return (
                <polyline
                  key={deg}
                  points={`${cos*13},${sin*13} ${cos*21+Math.cos(perp)*4},${sin*21+Math.sin(perp)*4} ${cos*30},${sin*30}`}
                  stroke="#ffe033" strokeWidth="2.5" fill="none"
                  strokeLinecap="round" strokeLinejoin="round"
                  className="scBolt"
                />
              );
            })}
            <text x="0" y="-36" textAnchor="middle" fontSize="14" className="scEmoji" style={{ userSelect: "none" }}>🛡️</text>
          </g>
        );
      })()}

      {/* -------- EAT HINT ARROWS -------- */}
      {eatHints?.map(({ fromPos, toPos }, i) => {
        const fromMarker = markers[fromPos];
        const toMarker   = markers[toPos];
        if (!fromMarker || !toMarker) return null;
        return (
          <EatHintArrow
            key={i}
            fromCoord={fromMarker.headCircle}
            toCoord={toMarker.headCircle}
            color="red"
          />
        );
      })}

      {/* -------- CAPTURE ANIMATION OVERLAY (topmost) -------- */}
      {captureInfo && (
        <g
          ref={captureOverlayRef}
          style={{
            pointerEvents: "none",
            transform: `translate(${captureInfo.fromX}px, ${captureInfo.fromY}px)`,
          }}
        >
          {/* Shockwave rings */}
          {[...Array(2)].map((_, i) => (
            <circle
              key={`r${i}`}
              className="cr"
              cx={0}
              cy={0}
              r={14}
              fill="none"
              stroke="white"
              strokeWidth={3 - i}
              style={{ opacity: 0 }}
            />
          ))}
          {/* Burst particles */}
          {[...Array(8)].map((_, i) => (
            <circle
              key={i}
              className="cp"
              cx={0}
              cy={0}
              r={7}
              fill={numberWiseColor[captureInfo.playerId]}
              style={{ opacity: 0 }}
            />
          ))}
          {/* Main goti — scale + rotation controlled separately */}
          <g
            ref={captureGotiRef}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          >
            <circle
              cx={0}
              cy={0}
              r={14}
              fill={numberWiseColor[captureInfo.playerId]}
              stroke="black"
              strokeWidth="1.5"
            />
          </g>
          {/* Crying emoji floats up during impact */}
          <text
            ref={captureEmojiRef}
            x={0}
            y={-20}
            textAnchor="middle"
            fontSize="22"
            style={{ opacity: 0, pointerEvents: "none", userSelect: "none" }}
          >😢</text>
        </g>
      )}

      {/* -------- FINISH ANIMATION OVERLAY (topmost) -------- */}
      {finishInfo && (
        <g
          ref={finishOverlayRef}
          style={{
            pointerEvents: "none",
            transform: `translate(${finishInfo.fromX}px, ${finishInfo.fromY}px)`,
          }}
        >
          {/* Star rays */}
          {[...Array(8)].map((_, i) => (
            <line
              key={`fr${i}`}
              className="fr"
              x1={0} y1={0} x2={0} y2={0}
              stroke="gold"
              strokeWidth="3.5"
              strokeLinecap="round"
              style={{ opacity: 0 }}
            />
          ))}
          {/* Shockwave rings */}
          {[...Array(3)].map((_, i) => (
            <circle
              key={`fring${i}`}
              className="fring"
              cx={0} cy={0} r={0}
              fill="none"
              stroke={i === 0 ? "gold" : "white"}
              strokeWidth={3 - i * 0.7}
              style={{ opacity: 0 }}
            />
          ))}
          {/* Main goti */}
          <g
            ref={finishGotiRef}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          >
            <circle
              cx={0} cy={0} r={13}
              fill={numberWiseColor[finishInfo.playerId]}
              stroke="white"
              strokeWidth="2"
            />
            <text
              x={0} y={0}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="13"
              fontWeight="900"
              fill="white"
              stroke="rgba(0,0,0,0.5)"
              strokeWidth="0.6"
              paintOrder="stroke"
            >
              {finishInfo.label}
            </text>
          </g>
        </g>
      )}
    </>
  );
}

export default memo(LudoMarkerGoti);
